// Group detail — rule-cards + members table (mockups/access-preview-
// v6.html "group-detail" view). The :groupId route param is the URL-
// encoded group_name (the SCIM-stable identifier the rest of the BFF
// keys off). When this page can't find the group in /api/groups it
// renders an inline "Group not found" so a stale link doesn't trap the
// operator.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ApiError, api, useApiErrorHandler } from "@/api";
import { useToast } from "@/components";
import { groupClient } from "@/data/clients/groupClient";
import { ruleClient } from "@/data/clients/ruleClient";
import { useRole } from "@/role";
import type { Rule } from "@/types/rule";
import {
  isRecentlyActive,
  relativeTime,
  roleClass,
  scopeSummary,
} from "./format";
import "../AdminShared.css";

// ---------------------------------------------------------------------------
// Wire types — shared shape with PeoplePage; duplicated here so the two
// pages can evolve independently if the BFF differentiates per-detail
// vs list responses.
// ---------------------------------------------------------------------------

interface ApiGroup {
  group_name: string;
  group_id?: string;
  display_name?: string;
  member_count?: number;
  synced_at?: string;
}

interface ApiUser {
  alias: string;
  ring_user_id?: string;
  display_name?: string;
  group_names?: string[];
  effective_role_id?: string;
  role_ids?: string[];
  effective_location_scope?: { mode: "ALL" | "ALLOW" | "DENY"; ids: string[] };
  /**
   * Optional secondary access surfaced in the "Effective access" cell as
   * "+ ROLE at LOCATION" (the mockup carries this for the IAD12 viewer
   * scope). The BFF may or may not populate it; we render only when present.
   */
  secondary_access?: { role_id: string; location_id: string };
  last_seen_at?: string;
  synced_at?: string;
}

interface GroupsResponse {
  items?: ApiGroup[];
  groups?: ApiGroup[];
}

function unwrap<T>(
  resp: { items?: T[] },
  legacyKey: "groups",
): T[] {
  if (Array.isArray(resp.items)) return resp.items;
  const r = resp as Record<string, unknown>;
  const legacy = r[legacyKey];
  return Array.isArray(legacy) ? (legacy as T[]) : [];
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function GroupDetailPage(): JSX.Element {
  const { groupId: rawGroupId } = useParams<{ groupId: string }>();
  const groupName = rawGroupId ?? "";
  const navigate = useNavigate();
  const handleApiError = useApiErrorHandler();
  const toast = useToast();
  const { currentRole } = useRole();

  const [group, setGroup] = useState<ApiGroup | null>(null);
  const [rules, setRules] = useState<Rule[]>([]);
  const [members, setMembers] = useState<ApiUser[]>([]);
  const [membersCursor, setMembersCursor] = useState<string | null>(null);
  const [loadingMoreMembers, setLoadingMoreMembers] = useState(false);
  // Synchronous in-flight guard. `loadingMoreMembers` (and the button's
  // `disabled`) only update on the next render, so two clicks dispatched in
  // the same tick both see the stale `false` + the same cursor closure and
  // double-append the same page. A ref flips immediately, before the await.
  const loadingMoreRef = useRef(false);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // Per-rule "affected users" count, lazily fetched. We only fall back to
  // this when the rule object doesn't carry an explicit users_count from
  // the list endpoint (which it usually doesn't on /api/admin/rules).
  const [ruleUserCounts, setRuleUserCounts] = useState<Record<string, number>>(
    {},
  );

  // Disable/enable in-flight tracking — avoids double-clicks while the
  // mutation is racing.
  const [togglingRuleId, setTogglingRuleId] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (groupName === "") return;
    setLoading(true);
    setNotFound(false);
    try {
      // Resolve the group (and its rules) first. We need the group's stable
      // group_id from the list before we can call the dedicated, paginated
      // members endpoint below.
      const [groupsResp, rulesResp] = await Promise.all([
        api.get<GroupsResponse>("/api/groups").catch((err: unknown) => {
          if (err instanceof ApiError && err.status === 404) {
            return { items: [] } as GroupsResponse;
          }
          throw err;
        }),
        ruleClient.list({ limit: 200 }),
      ]);

      const allGroups = unwrap<ApiGroup>(groupsResp, "groups");
      const found = allGroups.find((g) => g.group_name === groupName) ?? null;
      if (!found) {
        setNotFound(true);
        setGroup(null);
        setRules([]);
        setMembers([]);
        setMembersCursor(null);
        return;
      }
      setGroup(found);
      setRules(
        rulesResp.items.filter(
          (r) => r.group_match?.group_name === groupName,
        ),
      );

      // Members: dedicated, paginated per-group endpoint keyed off the
      // resolved group_id (group_name is the URL param but the API keys on
      // group_id). Reads only this group's partition — no full-tenant scan.
      const membersResp = await groupClient.listMembers(found.group_id ?? groupName);
      setMembers(
        membersResp.items.map((m) => ({
          alias: m.alias,
        })),
      );
      setMembersCursor(membersResp.next_cursor ?? null);
    } catch (err) {
      if (err instanceof ApiError) {
        handleApiError(err, { actionLabel: "Load group" });
      }
    } finally {
      setLoading(false);
    }
  }, [groupName, handleApiError]);

  useEffect(() => {
    if (currentRole === null) return;
    void load();
  }, [currentRole, load]);

  // Lazy "affected users" count per rule — only fetched when the list
  // endpoint didn't carry users_count. We do it once per rule and cache.
  useEffect(() => {
    if (rules.length === 0) return;
    let cancelled = false;
    void (async () => {
      const next: Record<string, number> = {};
      for (const r of rules) {
        if (ruleUserCounts[r.rule_id] !== undefined) continue;
        try {
          const resp = await ruleClient.listUsers(r.rule_id);
          if (cancelled) return;
          next[r.rule_id] = resp.items.length;
        } catch {
          // Non-fatal — leave the count as undefined; the UI shows "—".
        }
      }
      if (!cancelled && Object.keys(next).length > 0) {
        setRuleUserCounts((prev) => ({ ...prev, ...next }));
      }
    })();
    return () => {
      cancelled = true;
    };
    // We intentionally exclude ruleUserCounts from deps — re-running on
    // its own update would loop. The check inside the effect prevents
    // re-fetching for rules we've already seen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rules]);

  const handleToggleRule = useCallback(
    async (rule: Rule) => {
      setTogglingRuleId(rule.rule_id);
      try {
        const next = !rule.enabled;
        await ruleClient.update(
          rule.rule_id,
          { enabled: next },
          rule.version,
        );
        toast.success({
          title: next ? "Rule enabled" : "Rule disabled",
          description: rule.display_name,
        });
        void load();
      } catch (err) {
        if (err instanceof ApiError) {
          handleApiError(err, {
            actionLabel: `Toggle rule ${rule.display_name}`,
          });
        }
      } finally {
        setTogglingRuleId(null);
      }
    },
    [toast, handleApiError, load],
  );

  // Fetch the next page of members and append. Guarded against double-
  // invoke (the button is also disabled while in flight) and no-ops once
  // the cursor is exhausted.
  const loadMoreMembers = useCallback(async () => {
    if (loadingMoreRef.current || membersCursor === null || group === null) {
      return;
    }
    loadingMoreRef.current = true;
    setLoadingMoreMembers(true);
    try {
      const resp = await groupClient.listMembers(
        group.group_id ?? groupName,
        membersCursor,
      );
      setMembers((prev) => [
        ...prev,
        ...resp.items.map((m) => ({ alias: m.alias })),
      ]);
      setMembersCursor(resp.next_cursor ?? null);
    } catch (err) {
      if (err instanceof ApiError) {
        handleApiError(err, { actionLabel: "Load more members" });
      }
    } finally {
      loadingMoreRef.current = false;
      setLoadingMoreMembers(false);
    }
  }, [membersCursor, group, groupName, handleApiError]);

  // Tenant-wide site count for the "all <n> sites" sentence — derived
  // from the union of every rule's location_scope.ids (we don't have a
  // /api/locations call wired here, so this is the best signal available).
  const tenantSiteCount = useMemo(() => {
    const set = new Set<string>();
    for (const r of rules) for (const id of r.location_scope.ids) set.add(id);
    // If a rule is mode=ALL, its ids array is empty — but that rule still
    // implies "every site". We can't know the actual count, so default to
    // ids-set length and let the UI fall back to "all sites" without a
    // number when we have nothing.
    return set.size;
  }, [rules]);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  if (groupName === "") {
    return (
      <section className="admin-page">
        <header className="v6-page-header">
          <div className="breadcrumbs">
            <Link to="/admin/people">← Groups &amp; users</Link>
          </div>
          <h1>Group not specified</h1>
        </header>
      </section>
    );
  }

  if (notFound) {
    return (
      <section className="admin-page">
        <header className="v6-page-header">
          <div className="breadcrumbs">
            <Link to="/admin/people">← Groups &amp; users</Link>
          </div>
          <h1>Group not found</h1>
          <div className="caption">
            The group <code>{groupName}</code> doesn&apos;t exist or hasn&apos;t
            synced yet. <Link to="/admin/people">Back to People</Link>.
          </div>
        </header>
      </section>
    );
  }

  const displayName = group?.display_name ?? group?.group_name ?? groupName;
  const memberCount = group?.member_count ?? members.length;
  const lastSyncRel = group?.synced_at ? relativeTime(group.synced_at) : null;

  return (
    <section className="admin-page">
      <header className="v6-page-header">
        <div className="breadcrumbs">
          <Link to="/admin/people">← Groups &amp; users</Link>
        </div>
        <div className="row">
          <h1>{displayName}</h1>
          <div className="actions">
            <button
              type="button"
              className="v6-btn primary"
              onClick={() =>
                navigate(
                  `/admin/rules/new?group=${encodeURIComponent(groupName)}`,
                )
              }
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M19 11h-6V5h-2v6H5v2h6v6h2v-6h6z" />
              </svg>
              Add a rule
            </button>
          </div>
        </div>
        <div className="caption">
          <code
            style={{
              fontFamily:
                "'JetBrains Mono', 'SF Mono', Menlo, Consolas, monospace",
              fontSize: 13,
              background: "var(--ring-tertiary-bg)",
              padding: "2px 8px",
              borderRadius: 4,
            }}
          >
            {groupName}
          </code>{" "}
          · synced from your IdP via SCIM
          {lastSyncRel ? <> · last sync {lastSyncRel}</> : null}
          {" · "}
          <b>
            {memberCount} member{memberCount === 1 ? "" : "s"}
          </b>
        </div>
      </header>

      {/* ============== RULES THAT APPLY ============== */}
      <div className="v6-section-head">
        <span>Rules that apply to this group</span>
        <span className="sub">
          {loading
            ? "Loading…"
            : `${rules.length} rule${rules.length === 1 ? "" : "s"} · effective access composes them`}
        </span>
      </div>

      {loading ? (
        <RuleCardsSkeleton />
      ) : rules.length === 0 ? (
        <div
          className="admin-muted"
          style={{
            padding: 20,
            background: "var(--ring-tertiary-bg)",
            borderRadius: 8,
          }}
        >
          No rules apply to this group yet — its members are signed in but
          can&apos;t access anything until you add one.
        </div>
      ) : (
        <div className="v6-rule-cards">
          {rules.map((rule) => (
            <RuleCard
              key={rule.rule_id}
              rule={rule}
              tenantSiteCount={tenantSiteCount}
              affectedUsers={ruleUserCounts[rule.rule_id]}
              onEdit={() =>
                navigate(
                  `/admin/rules/${encodeURIComponent(rule.rule_id)}/edit`,
                )
              }
              onToggle={() => void handleToggleRule(rule)}
              toggling={togglingRuleId === rule.rule_id}
            />
          ))}
        </div>
      )}

      {/* ============== MEMBERS ============== */}
      <div className="v6-section-head" style={{ marginTop: 32 }}>
        <span>Members</span>
        <span className="sub">
          {loading
            ? "Loading…"
            : `${members.length} user${members.length === 1 ? "" : "s"} · synced from IdP — managed there, not here`}
        </span>
      </div>

      <table className="v6-table">
        <thead>
          <tr>
            <th>User</th>
            <th>Effective access</th>
            <th>Last seen</th>
            <th style={{ textAlign: "right" }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <MemberSkeletonRows />
          ) : members.length === 0 ? (
            <tr>
              <td colSpan={4} className="admin-muted" style={{ padding: 24 }}>
                No members synced for this group yet.
              </td>
            </tr>
          ) : (
            members.map((m) => (
              <MemberRow
                key={m.alias}
                user={m}
                onTrace={() =>
                  navigate(
                    `/admin/trace?alias=${encodeURIComponent(m.alias)}`,
                  )
                }
              />
            ))
          )}
        </tbody>
      </table>

      {membersCursor !== null ? (
        <div style={{ marginTop: 12, textAlign: "center" }}>
          <button
            type="button"
            className="v6-btn ghost"
            disabled={loadingMoreMembers}
            onClick={() => void loadMoreMembers()}
          >
            {loadingMoreMembers ? "Loading…" : "Load more members"}
          </button>
        </div>
      ) : null}

      <p
        style={{
          marginTop: 20,
          padding: "12px 16px",
          background: "var(--ring-tertiary-bg)",
          borderRadius: 8,
          fontSize: 13,
          color: "var(--ring-neutral-300)",
          lineHeight: 1.5,
        }}
      >
        Members are synced from your identity provider via SCIM. Add or remove
        users in your IdP — changes flow through automatically. The next sync
        is in <b>~12 minutes</b>.
      </p>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Rule card
// ---------------------------------------------------------------------------

interface RuleCardProps {
  rule: Rule;
  tenantSiteCount: number;
  affectedUsers: number | undefined;
  onEdit: () => void;
  onToggle: () => void;
  toggling: boolean;
}

function RuleCard({
  rule,
  tenantSiteCount,
  affectedUsers,
  onEdit,
  onToggle,
  toggling,
}: RuleCardProps): JSX.Element {
  const scopeText = scopeSummary(rule.location_scope, tenantSiteCount);
  const createdRel = relativeTime(rule.created_at);
  const modifiedRel = relativeTime(rule.updated_at ?? rule.created_at);
  const modifiedBy = rule.updated_by ?? rule.created_by;

  return (
    <div className="v6-rule-card">
      <div className="rule-card-head">
        <div className="rule-meta">
          <span className="rule-name">{rule.display_name}</span>
          <span
            className={`status-dot${rule.enabled ? " enabled" : ""}`}
            title={rule.enabled ? "Enabled" : "Disabled"}
          />
        </div>
        <div className="rule-card-actions">
          <button type="button" className="v6-btn ghost xs" onClick={onEdit}>
            Edit
          </button>
          <button
            type="button"
            className="v6-btn ghost xs"
            disabled={toggling}
            onClick={onToggle}
          >
            {rule.enabled ? "Disable" : "Enable"}
          </button>
        </div>
      </div>

      <div className="rule-sentence">
        <span className="rule-frag">members of this group</span>
        <span className="rule-glue">get</span>
        <span className={`v6-role-chip ${roleClass(rule.role_id)}`}>
          {rule.role_id.toUpperCase()}
        </span>
        <span className="rule-glue">at</span>
        <span className="rule-frag-bold">{scopeText}</span>
      </div>

      <div className="rule-card-foot">
        <span>
          Created {createdRel}
          {rule.created_by ? <> by {rule.created_by}</> : null}
        </span>
        <span className="dot-sep">·</span>
        <span>
          Last modified {modifiedRel}
          {modifiedBy && modifiedBy !== rule.created_by ? (
            <> by {modifiedBy}</>
          ) : null}
        </span>
        <span className="dot-sep">·</span>
        <span>
          Affects{" "}
          <b>
            {affectedUsers === undefined
              ? "—"
              : `${affectedUsers} user${affectedUsers === 1 ? "" : "s"}`}
          </b>
        </span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Member row
// ---------------------------------------------------------------------------

function initial(s: string): string {
  return (s || "?").charAt(0).toUpperCase();
}

interface MemberRowProps {
  user: ApiUser;
  onTrace: () => void;
}

function MemberRow({ user, onTrace }: MemberRowProps): JSX.Element {
  const role = user.effective_role_id ?? user.role_ids?.[0];
  const recent = isRecentlyActive(user.last_seen_at);
  return (
    <tr>
      <td className="alias">
        <span className="avatar-sm">{initial(user.alias)}</span>
        <div className="alias-stack">
          <b>{user.display_name ?? user.alias}</b>
          <span className="alias-id">{user.ring_user_id ?? "—"}</span>
        </div>
      </td>
      <td>
        {role ? (
          <span className={`v6-role-chip ${roleClass(role)}`}>
            {role.toUpperCase()}
          </span>
        ) : (
          <span className="v6-role-chip r-empty">— none —</span>
        )}
        {user.secondary_access ? (
          <span
            style={{
              fontSize: 12,
              color: "var(--ring-neutral-300)",
              marginLeft: 6,
            }}
          >
            + {user.secondary_access.role_id.toUpperCase()} at{" "}
            {user.secondary_access.location_id}
          </span>
        ) : null}
      </td>
      <td style={{ color: "var(--ring-neutral-300)" }}>
        {recent ? (
          <>
            <span className="dot-online" aria-hidden="true" />
            <span
              style={{
                color: "var(--ring-positive-500)",
                fontWeight: 500,
              }}
            >
              {relativeTime(user.last_seen_at)}
            </span>
          </>
        ) : (
          relativeTime(user.last_seen_at)
        )}
      </td>
      <td className="row-actions">
        <button type="button" className="v6-btn ghost xs" onClick={onTrace}>
          Trace →
        </button>
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Skeletons
// ---------------------------------------------------------------------------

function RuleCardsSkeleton(): JSX.Element {
  return (
    <div className="v6-rule-cards" aria-hidden="true">
      {[0, 1].map((i) => (
        <div key={i} className="v6-rule-card">
          <div className="rule-card-head">
            <div className="rule-meta">
              <div
                style={{
                  height: 18,
                  width: 240,
                  background: "var(--ring-tertiary-bg)",
                  borderRadius: 4,
                }}
              />
            </div>
          </div>
          <div
            style={{
              height: 44,
              background: "var(--ring-tertiary-bg)",
              borderRadius: 6,
            }}
          />
        </div>
      ))}
    </div>
  );
}

function MemberSkeletonRows(): JSX.Element {
  return (
    <>
      {[0, 1, 2].map((i) => (
        <tr key={i} aria-hidden="true">
          {[0, 1, 2, 3].map((c) => (
            <td key={c}>
              <div
                style={{
                  height: 14,
                  background: "var(--ring-tertiary-bg)",
                  borderRadius: 4,
                  width: c === 0 ? "55%" : c === 3 ? "30%" : "40%",
                }}
              />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}
